import { z } from 'zod'

const conditionSchema = z.object({
  field: z.enum(['target.name', 'target.id', 'target.manufacturer', 'target.model', 'target.serialNumber']),
  comparison: z.enum(['equals', 'not-equals', 'contains', 'not-contains', 'starts-with', 'ends-with']),
  value: z.string(),
}).refine((condition) => condition.field !== 'target.id'
  || condition.comparison === 'equals' || condition.comparison === 'not-equals')
const selectorGuiSchema = z.discriminatedUnion('mode', [
  z.object({ version: z.literal(1), mode: z.literal('matching'), groups: z.array(z.array(conditionSchema).min(1)).min(1) }),
  z.object({ version: z.literal(1), mode: z.literal('expression') }),
])

export type SelectorCondition = z.infer<typeof conditionSchema>
export type SelectorGui = z.infer<typeof selectorGuiSchema>

export const SELECTOR_COMPARISONS = [
  { value: 'equals', label: 'equals' },
  { value: 'not-equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'not-contains', label: 'does not contain' },
  { value: 'starts-with', label: 'starts with' },
  { value: 'ends-with', label: 'ends with' },
] as const

export type SelectorField = { value: SelectorCondition['field']; label: string; nullable?: boolean }
export const ENTITY_SELECTOR_FIELDS = [
  { value: 'target.name', label: 'Entity name' },
  { value: 'target.id', label: 'Entity ID' },
] satisfies readonly SelectorField[]
export const PRINTER_SELECTOR_FIELDS = [
  { value: 'target.manufacturer', label: 'Manufacturer', nullable: true },
  { value: 'target.model', label: 'Model', nullable: true },
  { value: 'target.serialNumber', label: 'Serial number', nullable: true },
  { value: 'target.id', label: 'Printer ID' },
] satisfies readonly SelectorField[]

function conditionExpression(condition: SelectorCondition, fields: readonly SelectorField[]) {
  const expression = comparisonExpression(condition)
  return fields.find((field) => field.value === condition.field)?.nullable
    ? `(has(${condition.field}) && ${expression})`
    : expression
}

function comparisonExpression(condition: SelectorCondition) {
  const normalizedValue = condition.field === 'target.id'
    ? condition.value.trim().toLowerCase()
    : condition.value
  const value = JSON.stringify(normalizedValue)
  switch (condition.comparison) {
    case 'equals': return `${condition.field} == ${value}`
    case 'not-equals': return `${condition.field} != ${value}`
    case 'not-contains': return `!${condition.field}.contains(${value})`
    case 'starts-with': return `${condition.field}.startsWith(${value})`
    case 'ends-with': return `${condition.field}.endsWith(${value})`
    default: return `${condition.field}.contains(${value})`
  }
}

export function selectorExpression(groups: SelectorCondition[][], fields: readonly SelectorField[] = ENTITY_SELECTOR_FIELDS) {
  return groups.map((group) => `(${group.map((condition) => conditionExpression(condition, fields)).join(' && ')})`).join(' || ')
}

export function readSelectorGui(
  raw: unknown,
  expression: string,
  fields: readonly SelectorField[] = ENTITY_SELECTOR_FIELDS,
): SelectorGui | null {
  const parsed = selectorGuiSchema.safeParse(raw)
  if (!parsed.success) return null
  const gui = parsed.data
  if (gui.mode !== 'matching') return gui
  const hasUnsupportedField = gui.groups.some((group) => group.some((condition) =>
    !fields.some((field) => field.value === condition.field),
  ))
  if (hasUnsupportedField || selectorExpression(gui.groups, fields) !== expression) return null
  return gui
}

export function policySelectorSummary(
  expression: string | null,
  raw: unknown,
  fields: readonly SelectorField[] = ENTITY_SELECTOR_FIELDS,
  allTargetsLabel = 'All entities in scope',
): string {
  if (!expression) return allTargetsLabel
  const gui = readSelectorGui(raw, expression, fields)
  if (gui?.mode !== 'matching') return expression
  const groups = gui.groups.map((group) => {
    const conditions = group.map((condition) => describeSelectorCondition(condition, fields)).join(' AND ')
    return `(${conditions})`
  })
  return groups.join(' OR ')
}

function describeSelectorCondition(condition: SelectorCondition, fields: readonly SelectorField[]) {
  const fieldLabel = fields.find((field) => field.value === condition.field)?.label ?? condition.field
  const comparisonLabel = SELECTOR_COMPARISONS.find((comparison) => comparison.value === condition.comparison)?.label ?? condition.comparison
  const value = condition.field === 'target.id' ? condition.value.trim().toLowerCase() : condition.value
  const formattedValue = JSON.stringify(value)
  return `${fieldLabel} ${comparisonLabel} ${formattedValue}`
}
